import React, { useMemo, useState } from 'react'
import {
  AudioPlayer,
  AUDIO_PLAYER_PREVIEW_STATE_LABELS,
  AUDIO_PLAYER_PREVIEW_STATES,
  type AudioPlayerPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AudioPlayerShowcase.css'

/** Short public sample for live preview (CORS-friendly MDN demo). */
const DEMO_AUDIO_SRC =
  'https://interactive-examples.mdn.mozilla.net/media/cc0-audio/t-rex-roar.mp3'

const AudioPlayerShowcase: React.FC = () => {
  const [src, setSrc] = useState(DEMO_AUDIO_SRC)
  const [hideSkip, setHideSkip] = useState(false)
  const [hideVolume, setHideVolume] = useState(false)
  const [hideSpeed, setHideSpeed] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<AudioPlayerPreviewState>('playing')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'audio-player-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="audio-player-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as AudioPlayerPreviewState,
                  )
                }
              >
                {AUDIO_PLAYER_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {AUDIO_PLAYER_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'audio-player-src',
            label: 'Audio URL',
            control: (
              <input
                id="audio-player-src"
                className="ui-field-control"
                type="text"
                value={src}
                onChange={(event) => setSrc(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'Controls',
        fields: [
          {
            id: 'audio-player-hide-skip',
            label: 'Hide skip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-skip"
                  type="checkbox"
                  checked={hideSkip}
                  onChange={(event) => setHideSkip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'audio-player-hide-volume',
            label: 'Hide volume',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-volume"
                  type="checkbox"
                  checked={hideVolume}
                  onChange={(event) => setHideVolume(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'audio-player-hide-speed',
            label: 'Hide speed',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="audio-player-hide-speed"
                  type="checkbox"
                  checked={hideSpeed}
                  onChange={(event) => setHideSpeed(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [hideSkip, hideSpeed, hideVolume, highlightedState, src],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Audio player"
        description="Compact call-recording player from Messages Call logs: skip, play/pause, scrubber, volume, and speed."
        stageClassName="ui-preview__stage--wide audio-player-showcase__stage-wrap"
      >
        <div className={`audio-player-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Matches the Call logs expanded-row player. Use a CORS-enabled
              audio URL for live playback.
            </p>
            <AudioPlayer
              src={src}
              hideSkip={hideSkip}
              hideVolume={hideVolume}
              hideSpeed={hideSpeed}
            />
          </section>

          <section className="audio-player-showcase__matrix">
            <h4 className="audio-player-showcase__section-title">States</h4>
            {AUDIO_PLAYER_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`audio-player-showcase__row${
                  state === highlightedState
                    ? ' audio-player-showcase__row--selected'
                    : ''
                }`}
              >
                <span className="audio-player-showcase__row-label">
                  {AUDIO_PLAYER_PREVIEW_STATE_LABELS[state]}
                </span>
                <AudioPlayer previewState={state} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Audio player"
        sections={propertySections}
      />
    </>
  )
}

export default AudioPlayerShowcase
